import { DateTime } from "luxon";

import { VideoClip } from "@app/analytics/types";

import useRecordingInfo from "./useRecordingInfo";

interface UseSessionClipParams {
  /** Unique key for the clip (will be used as the HTMLVideo's id) */
  key: string;
  sessionId: string;
  clipStart: DateTime;
  clipEnd: DateTime;
}

/**
 * Helper hook for getting a clip based on the clip start/end and session start time. Will handle
 * fetching the recording info and will return null if the user cannot access the recording or if
 * there is not enough information to be able to generate the clip.
 * */
const useSessionClip = ({
  key,
  sessionId,
  clipStart,
  clipEnd,
}: UseSessionClipParams) => {
  const { recordingInfo } = useRecordingInfo(sessionId, {
    enabled: Boolean(sessionId),
  });

  if (!recordingInfo || recordingInfo.status !== "ready") {
    return null;
  }

  const clipStartTime = Math.max(
    0,
    clipStart.diff(recordingInfo.startTime).as("seconds")
  );
  const clipEndTime = clipEnd.diff(recordingInfo.startTime).as("seconds");

  const duration = clipEndTime - clipStartTime;

  const clip: VideoClip = {
    id: key,
    start: clipStartTime,
    end: clipEndTime,
    duration,
  };

  return { clip, recordingInfo };
};

export default useSessionClip;
